import { FC, useCallback, useMemo, useState } from 'react';
import { MRT_Row, useMaterialReactTable } from 'material-react-table';
import { Box, IconButton, Tooltip } from '@mui/material';
import { styled } from '@mui/material/styles';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { format, parseISO } from 'date-fns';
import { PercentageDiscountDto } from 'src/generated/services/TFinancialApi';
import { useUpsertPercentageDiscount, useDeletePercentageDiscount } from 'src/use-cases/serviceProvider/useServiceProvider';
import { openToast, openToastError } from 'src/use-cases/toast/useToast';
import { beautifyEnum } from 'src/utils/utility';
import { CustomTable } from 'src/presentations/components/molecules/table/Table';
import IconTrash from 'src/presentations/components/atom/icons/Trash';
import IconNewEdit from 'src/presentations/components/atom/icons/newedit.svg';
import { ContractManagerColumnHeader } from '../ContractManagerColumnHeader';
import { CHARGE_CATEGORIES } from './constants';

const NoRadiusTableContainer = styled('div')(`
  height: 100%;
  & .MuiPaper-root.MuiTableContainer-root {
    border-radius: 0 !important;
    border: none !important;
    box-shadow: none !important;
  }
`);

const StyledDatePicker = styled(DatePicker)(({ theme }) => ({
  width: '100%',
  '& .MuiInputBase-root': {
    backgroundColor: 'transparent',
  },
  '& .MuiInputBase-input': {
    paddingTop: '4px!important',
    paddingBottom: '4px!important',
    paddingLeft: '0px!important',
    paddingRight: '0px!important',
    [theme.breakpoints.down(1520)]: {
      fontSize: '14px',
    },
  },
  '& .MuiFormHelperText-root ': {
    marginLeft: '0px!important',
  },
  '& .MuiOutlinedInput-notchedOutline': {
    border: '0',
    borderBottom: '1px solid #999999',
    borderRadius: '0',
  },
}));

const firstCellStyles = {
  boxShadow: '4px 0px 12px 0px rgba(89, 114, 130, 0.08)',
  borderLeft: '1px solid transparent',
  width: '120px',
  padding: '0 10px 0 10px !important',
  '& .MuiBox-root': {
    gap: '0!important',
    width: '90px',
    justifyContent: 'center',
    '& svg': {
      width: '20px!important',
    },
  },
};

const tableCellStyles = {
  borderBottom: '1px solid #E5E7EB !important',
};

// --- Cell renderers (extracted to avoid inline component definitions) ---
const IdCell = ({ row }: { row: any }) => (
  <span className='text-neutral-60 text-12'>{row.original.id ? `${String(row.original.id).substring(0, 8)}...` : '-'}</span>
);
const DiscountNameCell = ({ row }: { row: any }) => <>{row.original.discountName ?? '-'}</>;
const EligibleChargeDomainCell = ({ row }: { row: any }) => <>{row.original.eligibleChargeDomain ? beautifyEnum(row.original.eligibleChargeDomain) : '-'}</>;
const EligibleChargeSubDomainCell = ({ row }: { row: any }) => (
  <>{row.original.eligibleChargeSubDomain ? beautifyEnum(row.original.eligibleChargeSubDomain) : '-'}</>
);
const RateCell = ({ row }: { row: any }) => <>{row.original.rate !== undefined && row.original.rate !== null ? `${row.original.rate}` : '-'}</>;
const ValidityStartDateCell = ({ row }: { row: any }) => (
  <>{row.original.validityStartDate ? format(parseISO(row.original.validityStartDate), 'dd-MMM-yyyy') : '-'}</>
);
const ValidityEndDateCell = ({ row }: { row: any }) => (
  <>{row.original.validityEndDate ? format(parseISO(row.original.validityEndDate), 'dd-MMM-yyyy') : '-'}</>
);
const ChargeUnitCell = ({ row }: { row: any }) => <>{row.original.chargeUnit ?? '-'}</>;
const MinWeightCell = ({ row }: { row: any }) => <>{row.original.minimumChargeableWeight ?? '-'}</>;
const MaxWeightCell = ({ row }: { row: any }) => <>{row.original.maximumChargeableWeight ?? '-'}</>;

// --- Date picker Edit renderers (extracted; receive MRT render-prop args directly) ---
const ValidityStartDateEdit = ({ column, row, table: t }: { column: any; row: any; table: any }) => {
  const { creatingRow, editingRow } = t.getState();
  const isCreating = creatingRow?.id === row.id;
  const isEditing = editingRow?.id === row.id;
  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <StyledDatePicker
        defaultValue={row.original.validityStartDate ? parseISO(row.original.validityStartDate) : null}
        format='dd-MMM-yyyy'
        onChange={(value) => {
          // eslint-disable-next-line no-underscore-dangle
          row._valuesCache[column.id] = value;
          if (isCreating) t.setCreatingRow(row);
          else if (isEditing) t.setEditingRow(row);
        }}
        slotProps={{ textField: { placeholder: '10-Dec-2025' } }}
      />
    </LocalizationProvider>
  );
};

const ValidityEndDateEdit = ({ column, row, table: t }: { column: any; row: any; table: any }) => {
  const { creatingRow, editingRow } = t.getState();
  const isCreating = creatingRow?.id === row.id;
  const isEditing = editingRow?.id === row.id;
  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <StyledDatePicker
        defaultValue={row.original.validityEndDate ? parseISO(row.original.validityEndDate) : null}
        format='dd-MMM-yyyy'
        onChange={(value) => {
          // eslint-disable-next-line no-underscore-dangle
          row._valuesCache[column.id] = value;
          if (isCreating) t.setCreatingRow(row);
          else if (isEditing) t.setEditingRow(row);
        }}
        slotProps={{ textField: { placeholder: '10-Dec-2025' } }}
      />
    </LocalizationProvider>
  );
};

// --- Header renderer (extracted; tableData passed as prop from column useMemo) ---
interface PDColumnHeaderProps {
  column: any;
  accessorKey: string;
  tableData: PercentageDiscountDto[];
}
const PDColumnHeader = ({ column, accessorKey, tableData }: PDColumnHeaderProps) => (
  <ContractManagerColumnHeader column={column} accessorKey={accessorKey} originalData={tableData} currentFilters={[]} onSort={() => {}} onFilter={() => {}} />
);

interface ITablePercentageDiscountProps {
  contractFlow: any;
  tableRef: React.MutableRefObject<any>;
  isAdmin?: boolean;
}

export const TablePercentageDiscount: FC<ITablePercentageDiscountProps> = ({ contractFlow, tableRef, isAdmin }) => {
  const [validationErrors, setValidationErrors] = useState<Record<string, string | undefined>>({});
  const [isSaving, setIsSaving] = useState(false);

  const { mutateAsync: upsertPercentageDiscount } = useUpsertPercentageDiscount();
  const { mutateAsync: deletePercentageDiscount } = useDeletePercentageDiscount();

  const tableData: PercentageDiscountDto[] = useMemo(() => contractFlow?.percentageDiscounts ?? [], [contractFlow]);

  const handleDelete = async (row: MRT_Row<PercentageDiscountDto>) => {
    // eslint-disable-next-line no-alert
    if (globalThis.confirm('Are you sure you want to delete this percentage discount?')) {
      try {
        await deletePercentageDiscount({
          contractFlowId: contractFlow?.id,
          percentageDiscountId: row.original.id,
        });
        openToast({
          toastType: 'success',
          title: 'Success',
          description: 'Percentage discount deleted!',
        });
      } catch (error) {
        openToastError(error instanceof Error ? error : new Error('Percentage discount delete failed'));
      }
    }
  };

  const handleUpsert = useCallback(
    async ({ row, values, table: t }: { row: MRT_Row<any>; values: Record<string, any>; table: any }) => {
      const errors: Record<string, string | undefined> = {};
      if (!values.discountName || values.discountName.trim() === '') errors.discountName = 'Required';
      if (!values.rate && values.rate !== 0) errors.rate = 'Required';
      if (Object.values(errors).some(Boolean)) {
        setValidationErrors(errors);
        return;
      }

      setIsSaving(true);
      try {
        const payload: PercentageDiscountDto & { contractFlowId: string } = {
          contractFlowId: contractFlow?.id,
          id: row.original?.id || undefined,
          discountName: values.discountName ?? row.original?.discountName ?? '',
          eligibleChargeDomain: values.eligibleChargeDomain || undefined,
          eligibleChargeSubDomain: values.eligibleChargeSubDomain || undefined,
          rate: Number.parseFloat(values.rate),
          validityStartDate: values.validityStartDate ? format(new Date(values.validityStartDate), 'yyyy-MM-dd') : undefined,
          validityEndDate: values.validityEndDate ? format(new Date(values.validityEndDate), 'yyyy-MM-dd') : undefined,
          chargeUnit: values.chargeUnit || undefined,
          minimumChargeableWeight:
            values.minimumChargeableWeight !== '' && values.minimumChargeableWeight !== undefined ? Number(values.minimumChargeableWeight) : undefined,
          maximumChargeableWeight:
            values.maximumChargeableWeight !== '' && values.maximumChargeableWeight !== undefined ? Number(values.maximumChargeableWeight) : undefined,
        };
        await upsertPercentageDiscount(payload);
        openToast({
          toastType: 'success',
          title: 'Success',
          description: 'Percentage discount saved!',
        });
      } catch (error) {
        openToastError(error instanceof Error ? error : new Error('Percentage discount save failed'));
      } finally {
        setIsSaving(false);
        t.setCreatingRow(null);
        t.setEditingRow(null);
      }
    },
    [contractFlow, upsertPercentageDiscount]
  );

  const columns = useMemo(
    () => [
      {
        accessorKey: 'id',
        header: 'ID',
        enableEditing: false,
        enableSorting: false,
        size: 80,
        // eslint-disable-next-line react/no-unstable-nested-components
        Header: ({ column }) => <PDColumnHeader column={column} accessorKey='id' tableData={tableData} />,
        Cell: IdCell,
      },
      {
        accessorKey: 'discountName',
        header: 'DISCOUNT NAME',
        enableSorting: false,
        muiEditTextFieldProps: {
          error: !!validationErrors?.discountName,
          helperText: validationErrors?.discountName,
          onFocus: () => setValidationErrors((prev) => ({ ...prev, discountName: undefined })),
        },
        // eslint-disable-next-line react/no-unstable-nested-components
        Header: ({ column }) => <PDColumnHeader column={column} accessorKey='discountName' tableData={tableData} />,
        Cell: DiscountNameCell,
      },
      {
        accessorKey: 'eligibleChargeDomain',
        header: 'CHARGE DOMAIN',
        enableSorting: false,
        editVariant: 'select' as const,
        editSelectOptions: [{ label: 'Empty', value: '' }, ...CHARGE_CATEGORIES],
        muiEditTextFieldProps: {
          select: true,
          error: !!validationErrors?.eligibleChargeDomain,
          helperText: validationErrors?.eligibleChargeDomain,
        },
        // eslint-disable-next-line react/no-unstable-nested-components
        Header: ({ column }) => <PDColumnHeader column={column} accessorKey='eligibleChargeDomain' tableData={tableData} />,
        Cell: EligibleChargeDomainCell,
      },
      {
        accessorKey: 'eligibleChargeSubDomain',
        header: 'SUB DOMAIN',
        enableSorting: false,
        muiEditTextFieldProps: {
          error: !!validationErrors?.eligibleChargeSubDomain,
          helperText: validationErrors?.eligibleChargeSubDomain,
        },
        // eslint-disable-next-line react/no-unstable-nested-components
        Header: ({ column }) => <PDColumnHeader column={column} accessorKey='eligibleChargeSubDomain' tableData={tableData} />,
        Cell: EligibleChargeSubDomainCell,
      },
      {
        accessorKey: 'rate',
        header: 'RATE',
        enableSorting: false,
        muiEditTextFieldProps: {
          type: 'number',
          error: !!validationErrors?.rate,
          helperText: validationErrors?.rate,
          onFocus: () => setValidationErrors((prev) => ({ ...prev, rate: undefined })),
        },
        // eslint-disable-next-line react/no-unstable-nested-components
        Header: ({ column }) => <PDColumnHeader column={column} accessorKey='rate' tableData={tableData} />,
        Cell: RateCell,
      },
      {
        accessorKey: 'validityStartDate',
        header: 'START DATE',
        enableSorting: false,
        // eslint-disable-next-line react/no-unstable-nested-components
        Header: ({ column }) => <PDColumnHeader column={column} accessorKey='validityStartDate' tableData={tableData} />,
        Cell: ValidityStartDateCell,
        Edit: ValidityStartDateEdit,
      },
      {
        accessorKey: 'validityEndDate',
        header: 'END DATE',
        enableSorting: false,
        // eslint-disable-next-line react/no-unstable-nested-components
        Header: ({ column }) => <PDColumnHeader column={column} accessorKey='validityEndDate' tableData={tableData} />,
        Cell: ValidityEndDateCell,
        Edit: ValidityEndDateEdit,
      },
      {
        accessorKey: 'chargeUnit',
        header: 'CHARGE UNIT',
        enableSorting: false,
        editVariant: 'select' as const,
        editSelectOptions: [
          { label: 'Empty', value: '' },
          { label: 'KG', value: 'KG' },
          { label: 'CBM', value: 'CBM' },
          { label: 'PKG', value: 'PKG' },
          { label: 'LBS', value: 'LBS' },
          { label: 'REF', value: 'REF' },
        ],
        muiEditTextFieldProps: { select: true },
        // eslint-disable-next-line react/no-unstable-nested-components
        Header: ({ column }) => <PDColumnHeader column={column} accessorKey='chargeUnit' tableData={tableData} />,
        Cell: ChargeUnitCell,
      },
      {
        accessorKey: 'minimumChargeableWeight',
        header: 'MIN CHARGEABLE WEIGHT',
        enableSorting: false,
        size: 210,
        minSize: 210,
        muiEditTextFieldProps: { type: 'number' },
        // eslint-disable-next-line react/no-unstable-nested-components
        Header: ({ column }) => <PDColumnHeader column={column} accessorKey='minimumChargeableWeight' tableData={tableData} />,
        Cell: MinWeightCell,
      },
      {
        accessorKey: 'maximumChargeableWeight',
        header: 'MAX CHARGEABLE WEIGHT',
        enableSorting: false,
        size: 210,
        minSize: 210,
        muiEditTextFieldProps: { type: 'number' },
        // eslint-disable-next-line react/no-unstable-nested-components
        Header: ({ column }) => <PDColumnHeader column={column} accessorKey='maximumChargeableWeight' tableData={tableData} />,
        Cell: MaxWeightCell,
      },
    ],
    [validationErrors, tableData]
  );

  const table = useMaterialReactTable({
    enableStickyHeader: true,
    enableSorting: false,
    enablePinning: false,
    enableRowDragging: false,
    enableColumnActions: false,
    enablePagination: false,
    enableTopToolbar: false,
    enableBottomToolbar: false,
    enableEditing: isAdmin,
    createDisplayMode: 'row',
    editDisplayMode: 'row',
    onCreatingRowSave: handleUpsert as any,
    onEditingRowSave: handleUpsert as any,
    enableRowSelection: false,
    enableMultiRowSelection: false,
    enableColumnPinning: true,
    positionActionsColumn: 'first',
    initialState: {
      columnPinning: { left: ['mrt-row-actions'] },
      columnVisibility: { id: false },
    },
    displayColumnDefOptions: {
      'mrt-row-actions': {
        header: '',
        size: 120,
      },
    },
    muiTableHeadProps: {
      sx: {
        ...(isAdmin && { '& th:first-child': firstCellStyles }),
      },
    },
    muiTableBodyProps: {
      sx: {
        ...(isAdmin && { '& td:first-child': firstCellStyles }),
      },
    },
    muiTableProps: {
      sx: { borderCollapse: 'separate', minWidth: '1200px' },
    },
    muiTablePaperProps: {
      sx: { borderRadius: '0 !important' },
    },
    muiTableContainerProps: {
      sx: { borderRadius: '0 !important' },
      className: 'no-border-radius',
    },
    muiTableBodyCellProps: { sx: tableCellStyles },
    muiTableHeadCellProps: { sx: { ...tableCellStyles, whiteSpace: 'nowrap' } },
    data: tableData,
    columns,
    state: { isSaving },
    renderRowActions: isAdmin
      ? ({ row, table: t }) => (
          <Box sx={{ display: 'flex' }}>
            <Tooltip title='Edit'>
              <IconButton onClick={() => t.setEditingRow(row)}>
                <img src={IconNewEdit} alt='Edit' />
              </IconButton>
            </Tooltip>
            <Tooltip title='Delete'>
              <IconButton color='error' onClick={() => handleDelete(row)}>
                <IconTrash />
              </IconButton>
            </Tooltip>
          </Box>
        )
      : undefined,
  });

  tableRef.current = table;

  return (
    <div className='h-full'>
      <NoRadiusTableContainer>
        <CustomTable
          table={table}
          tableContainerProps={{
            sx: {
              height: '100%',
              maxHeight: '100%',
              overflow: 'auto',
              '& .MuiTable-root': { borderCollapse: 'separate', minWidth: '1200px' },
              '& .MuiTableRow-head': { backgroundColor: '#ffffff' },
              '&::-webkit-scrollbar': { height: '12px', width: '12px', backgroundColor: '#f1f1f1' },
              '&::-webkit-scrollbar-track': { backgroundColor: '#f1f1f1' },
              '&::-webkit-scrollbar-thumb': {
                backgroundColor: '#BDBDBD',
                borderRadius: '6px',
                border: '3px solid #f1f1f1',
                backgroundClip: 'content-box',
                '&:hover': { backgroundColor: '#999999' },
              },
            },
          }}
        />
      </NoRadiusTableContainer>
    </div>
  );
};
